import React, { useState, useMemo } from "react";
import {
    Box,
    Paper,
    IconButton,
    Stack,
    Button,
    Slider
} from "@mui/material";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
    LineChart,
    Line,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    CartesianGrid,
    XAxis // Añadido para mejorar precisión de clics
} from "recharts";

const PlayBack = ({
    isPlaying,
    startPlayback,
    pausePlayback,
    playPoints,
    speed,
    changeSpeed,
    playIndex,
    setPlayIndex,
    segmentRef,
    tRef,
    setMarkerPosition,
    currentPoint,
    rumboTexto,
    speedRanges
}) => {

    const [expanded, setExpanded] = useState(false);

    const chartData = useMemo(() => {
        return playPoints.map((p, i) => ({
            index: i, // Usamos el índice como clave principal para la ReferenceLine
            time: new Date(p.timestamp).toLocaleTimeString(),
            speed: p.velocidad ?? 0
        }));
    }, [playPoints]);

    const goToIndex = (index, resume = false) => {
        if (!playPoints.length || playPoints.length < 2) return;

        let targetIndex = Math.round(index); // Asegurar que sea entero
        if (targetIndex >= playPoints.length - 1) {
            targetIndex = playPoints.length - 2;
        }
        if (targetIndex < 0) targetIndex = 0;

        // 1. Sincronización inmediata de referencias del motor
        segmentRef.current = targetIndex;
        tRef.current = 0;

        // 2. Actualizar estado visual (UI)
        setPlayIndex(targetIndex);

        const p = playPoints[targetIndex];
        if (p) {
            setMarkerPosition({
                lat: p.lat,
                lng: p.lng
            });
        }

        // 3. Manejo de reproducción
        if (resume && !isPlaying) {
            startPlayback();
        } else if (!resume && isPlaying) {
            pausePlayback();
        }
    };

    const handleGraphClick = (e) => {
        // Recharts devuelve el índice en activeTooltipIndex o activeLabel
        if (!e || e.activeTooltipIndex === undefined) return;
        
        const targetIndex = e.activeTooltipIndex;
        goToIndex(targetIndex, isPlaying);
    };

    const getRangeLabel = (range) => {
        if (range.max === Infinity) return `${range.min}+`;
        return `${range.min}-${range.max}`;
    };

    return (
        <>
            {/* CONTROLES */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 1.8,
                    py: 1,
                    borderRadius: "12px",
                    ml: { xs: 0, md: "auto" },
                    width: { xs: "100%", md: "auto" },
                    minWidth: { xs: "100%", md: 420 },
                    flexShrink: 1,
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                    background: "#FFFFFF"
                }}
            >
                {/* INFO */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        ml: 1,
                        fontSize: 11,
                        color: "#334155",
                        alignItems: "center",
                        minWidth: 0,
                        flexShrink: 1
                    }}
                >
                    <Box sx={{
                        maxWidth: 70,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}>
                        🧭 {rumboTexto(currentPoint?.rumbo)}
                    </Box>
                    <span>🚗 {currentPoint?.velocidad ?? "-"} km/h</span>
                    <span>
                        🕒 {currentPoint
                            ? new Date(currentPoint.timestamp).toLocaleTimeString()
                            : "-"}
                    </span>
                </Box>

                <IconButton
                    onClick={isPlaying ? pausePlayback : startPlayback}
                    disabled={!playPoints.length}
                    sx={{
                        width: 34,
                        height: 34,
                        background: "rgba(47,164,184,0.12)",
                        color: "#2FA4B8"
                    }}
                >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>

                <Stack direction="row" spacing={0.5}>
                    {[2, 8, 16, 32].map(s => (
                        <Button
                            key={s}
                            size="small"
                            variant={speed === s ? "contained" : "outlined"}
                            onClick={() => changeSpeed(s)}
                            sx={{ minWidth: 35 }}
                        >
                            x{s}
                        </Button>
                    ))}
                </Stack>

                <Box sx={{ flex: 1, minWidth: 120 }}>
                    <Slider
                        size="small"
                        min={0}
                        max={playPoints.length ? playPoints.length - 1 : 0}
                        value={playIndex}
                        onChange={(e, v) => goToIndex(v, false)}
                    />
                </Box>

                <IconButton
                    onClick={() => setExpanded(prev => !prev)}
                    sx={{ width: 30, height: 30, ml: "auto" }}
                >
                    <ExpandMoreIcon
                        sx={{
                            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "0.3s"
                        }}
                    />
                </IconButton>
            </Paper>

            {/* GRÁFICO */}
            {expanded && (
                <Box
                    sx={{
                        width: "100%",
                        mt: 1,
                        px: 1,
                        py: 1,
                        borderTop: "1px solid #E5E7EB",
                        background: "#FAFAFA",
                        outline: "none",
                        "& *:focus": { outline: "none" }
                    }}
                >
                    <ResponsiveContainer width="100%" height={130}>
                        <LineChart
                            data={chartData}
                            onClick={handleGraphClick}
                            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                            style={{ outline: "none", cursor: "pointer" }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            
                            {/* Eje X oculto para sincronizar los índices del gráfico */}
                            <XAxis dataKey="index" hide />
                            
                            <YAxis
                                width={45}
                                tick={{ fontSize: 10 }}
                                domain={[0, 'dataMax + 10']}
                            />

                            <Tooltip
                                isAnimationActive={false}
                                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                                formatter={(value) => [`${value} km/h`, "Velocidad"]}
                            />

                            {/* LÍNEAS DE RANGO DE VELOCIDAD */}
                            {speedRanges?.map((r, i) => (
                                <ReferenceLine
                                    key={`range-${i}`}
                                    y={r.min}
                                    stroke={r.color}
                                    strokeDasharray="4 4"
                                    label={{
                                        value: getRangeLabel(r),
                                        position: "right",
                                        fontSize: 9,
                                        fill: r.color
                                    }}
                                />
                            ))}

                            <Line
                                type="monotone"
                                dataKey="speed"
                                stroke="#2FA4B8"
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false} // Desactivado para que la línea negra no tenga lag
                            />

                            {/* ESTA ES LA BARRA QUE DEBE MOVERSE */}
                            <ReferenceLine
                                x={playIndex}
                                stroke="#000"
                                strokeWidth={2}
                                label={{ 
                                    value: '📍', 
                                    position: 'top', 
                                    fontSize: 12 
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </>
    );
};

export default PlayBack;